import { FileActionButton } from "../file-action/file-action-controls";
import React, { useState, useEffect } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ColumnGroup } from "primereact/columngroup";
import { Row } from "primereact/row";
import { MultiSelect } from "primereact/multiselect";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";
import oilProductionService from "../../services/oilProduction/oilProductionservice";
import "./RawMaterialStockDashboard.scss";

interface PosAllocation {
    posId: number; posName: string; stock: number; availableStock: number;
}
interface AllocatedProduct {
    productId: number; productName: string; stock: number;
    availableStock: number; stockReductionFactor: number;
    allocatedVolume: number; unitPrice: number;
    posAllocations: PosAllocation[];
}
interface ProductionBatch {
    batchId: number;
    sourceTypeId: number;
    sourceTypeName: string;
    batchStock: number;
    batchAvailableStock: number;
    batchAllocatedStock: number;
    batchInPosAvailableStock: number;
    allocatedProducts: AllocatedProduct[];
}
interface ByProduct {
    byProductId: number; byProductName: string; unit: string;
    subCategoryId: number; totalProduced: number; usedInBatch: number;
    pendingStock: number; batchAvailableStock: number;
    batchAllocatedStock: number; poInwardedAvailable: number;
    pendingValue: number; batchAvailableValue: number; poInwardedValue: number;
    totalWipQty: number; unitPrice: number; totalWipValue: number;
    yetToCreateBatch: number; yetToCreateBatchUom: string;
    yetToInward: number; yetToInwardUom: string;
    inPos: number; inPosUom: string;
    allocatedProducts: AllocatedProduct[];
    batches: ProductionBatch[];
}
interface WipProductionMainStock {
    rawMaterialId: number; rawMaterialName: string;
    totalPurchasedQty: number; totalUtilisedQty: number;
    remainingStock: number; remainingStockValue: number;
    rmUom: string; byProducts: ByProduct[];
}

const isOil = (bp: ByProduct | { unit: string }) => bp.unit?.toLowerCase() === 'litre';

interface FlatRow {
    byProductId: number;
    byProductName: string;
    unit: string;
    isOil: boolean;
    rmStock: number;
    rmUom: string;
    mappedRMs: { name: string; stock: number; value: number }[];
    yetToCreateBatch: number;
    yetToCreateBatchUom: string;
    yetToInward: number;
    yetToInwardUom: string;
    inPos: number;
    inPosUom: string;
    totalWipQty: number;
    unitPrice: number;
    totalWipValue: number;
    pendingValue: number;
    batchAvailableValue: number;
    posValue: number;
    productionBatches: (ProductionBatch & { rmName: string })[];
    byProducts: (ByProduct & { rmName: string })[];
}

const f = (n: number) => {
    if (n === null || n === undefined || isNaN(n)) return '0.00';
    return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const SmartTooltip: React.FC<{
    title: string;
    value: number;
    unit: string;
    items: any[];
    renderItem: (item: any, idx: number) => React.ReactNode;
    subtitle?: React.ReactNode;
}> = ({ title, value, unit, items, renderItem, subtitle }) => {
    const [page, setPage] = useState(0);
    const pageSize = 5;
    const totalPages = Math.ceil(items.length / pageSize);
    const visibleItems = items.slice(page * pageSize, (page + 1) * pageSize);

    return (
        <div className="custom-tt" onClick={(e) => e.stopPropagation()}>
            <div className="custom-tt-inner">
                <div className="tt-bp">
                    <div className="tt-bp-name" style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'stretch' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px', width: '100%' }}>
                            <span style={{ flex: 1, whiteSpace: 'normal', wordBreak: 'break-word' }}>{title}</span>
                            <span className={`tt-bp-val ${value < 0 ? 'neg' : ''}`}>
                                {value < 0 && '-'} {f(Math.abs(value))} {unit}
                            </span>
                        </div>
                        {subtitle && (
                            <div style={{ alignSelf: 'flex-end', fontSize: '0.68rem', color: '#94a3b8', fontWeight: '500', marginTop: '2px', paddingRight: '2px' }}>
                                {subtitle}
                            </div>
                        )}
                    </div>

                    <div className="tt-allocs">
                        {visibleItems.map(renderItem)}
                    </div>

                    {totalPages > 1 && (
                        <div className="tt-pager">
                            <button
                                className="tt-pager-btn"
                                disabled={page === 0}
                                onClick={(e) => { e.preventDefault(); setPage(p => p - 1); }}
                            >
                                <i className="pi pi-chevron-left"></i>
                            </button>
                            <span className="tt-pager-info">{page + 1} / {totalPages}</span>
                            <button
                                className="tt-pager-btn"
                                disabled={page === totalPages - 1}
                                onClick={(e) => { e.preventDefault(); setPage(p => p + 1); }}
                            >
                                <i className="pi pi-chevron-right"></i>
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const WipProductionMain: React.FC = () => {
    const [allRows, setAllRows] = useState<FlatRow[]>([]);
    const [selectedByProducts, setSelectedByProducts] = useState<string[]>([]);
    const [selectedRawMaterials, setSelectedRawMaterials] = useState<string[]>([]);
    const [selectedRmUoms, setSelectedRmUoms] = useState<string[]>([]);
    const [selectedYetToCreateUoms, setSelectedYetToCreateUoms] = useState<string[]>([]);
    const [selectedYetToInwardUoms, setSelectedYetToInwardUoms] = useState<string[]>([]);
    const [selectedInPosUoms, setSelectedInPosUoms] = useState<string[]>([]);
    const [selectedTotalWipUoms, setSelectedTotalWipUoms] = useState<string[]>([]);
    const [posOptions, setPosOptions] = useState<{ label: string; value: number }[]>([]);
    const [selectedPosIds, setSelectedPosIds] = useState<number[]>([]);
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [first, setFirst] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(15);

    useEffect(() => { fetchMUPlants(); }, []);
    useEffect(() => {
        if (selectedPosIds.length > 0) fetchStockData();
        else setAllRows([]);
    }, [selectedPosIds]);

    const fetchMUPlants = async () => {
        try {
            const res = await oilProductionService.GetMUPlantName();
            if (res && Array.isArray(res)) {
                const opts = res.map((mu: any) => ({
                    label: mu.address2 ?? mu.posName ?? `Unit-${mu.posID}`,
                    value: Number(mu.posID),
                }));
                setPosOptions(opts);
                setSelectedPosIds(opts.map((o: any) => o.value));
            } else {
                setPosOptions([]);
                setSelectedPosIds([]);
            }
        } finally { setInitialLoading(false); }
    };

    const sumPos = (bps: ByProduct[]) => {
        let total = 0;
        bps.forEach(bp => {
            (bp.allocatedProducts ?? []).forEach(ap => {
                total += (ap.allocatedVolume || 0);
            });
        });
        return total;
    };

    const fetchStockData = async () => {
        try {
            setLoading(true);
            const res: WipProductionMainStock[] = await oilProductionService.GetWipProductionMainStock(selectedPosIds);
            const bpMap = new Map<string, FlatRow>();

            (Array.isArray(res) ? res : []).forEach(rm => {
                (rm.byProducts ?? []).forEach(bp => {
                    const key = bp.byProductName;
                    if (!bpMap.has(key)) {
                        bpMap.set(key, {
                            byProductId: bp.byProductId,
                            byProductName: bp.byProductName,
                            unit: bp.unit,
                            isOil: isOil(bp),
                            rmStock: 0,
                            rmUom: rm.rmUom || "KG",
                            mappedRMs: [],
                            yetToCreateBatch: 0,
                            yetToCreateBatchUom: bp.yetToCreateBatchUom || (isOil(bp) ? "L" : "KG"),
                            yetToInward: 0,
                            yetToInwardUom: bp.yetToInwardUom || (isOil(bp) ? "L" : "KG"),
                            inPos: 0,
                            inPosUom: bp.inPosUom || (isOil(bp) ? "L" : "KG"),
                            totalWipQty: 0,
                            unitPrice: 0,
                            totalWipValue: 0,
                            pendingValue: 0,
                            batchAvailableValue: 0,
                            posValue: 0,
                            productionBatches: [],
                            byProducts: []
                        });
                    }
                    const row = bpMap.get(key)!;

                    if (!row.mappedRMs.some(m => m.name === rm.rawMaterialName)) {
                        row.rmStock += rm.remainingStock;
                        row.mappedRMs.push({ name: rm.rawMaterialName, stock: rm.remainingStock, value: rm.remainingStockValue || 0 });
                    }

                    row.yetToCreateBatch += bp.yetToCreateBatch;
                    row.yetToInward += bp.yetToInward;
                    row.inPos += bp.inPos;
                    row.totalWipQty += bp.totalWipQty;

                    // Simple average unit price or sum of values divided by total quantity
                    row.pendingValue += (bp.pendingValue || 0);
                    row.batchAvailableValue += (bp.batchAvailableValue || 0);
                    row.posValue += (bp.poInwardedValue || 0);

                    row.byProducts.push({ ...bp, rmName: rm.rawMaterialName });

                    if (bp.batches) {
                        bp.batches.forEach(b => {
                            row.productionBatches.push({ ...b, rmName: rm.rawMaterialName });
                        });
                    }
                });
            });

            const flat = Array.from(bpMap.values()).map(row => {
                const totalWipVal = row.totalWipQty * row.byProducts[0]?.unitPrice * 0.80;
                return {
                    ...row,
                    unitPrice: row.byProducts[0]?.unitPrice || 0,
                    totalWipValue: totalWipVal
                };
            }).sort((a, b) => {
                if (a.isOil !== b.isOil) return a.isOil ? -1 : 1;
                return b.rmStock - a.rmStock;
            });

            setAllRows(flat);
        } catch (e) {
            console.error(e);
        } finally { setLoading(false); }
    };

    const displayRows = React.useMemo(() => {
        let filtered = allRows;
        if (selectedByProducts && selectedByProducts.length > 0) {
            filtered = filtered.filter(r => selectedByProducts.includes(r.byProductName));
        }
        if (selectedRawMaterials && selectedRawMaterials.length > 0) {
            filtered = filtered.filter(r =>
                r.mappedRMs.some(rm => selectedRawMaterials.includes(rm.name))
            );
        }
        if (selectedRmUoms && selectedRmUoms.length > 0) {
            filtered = filtered.filter(r => selectedRmUoms.includes(r.rmUom));
        }
        if (selectedYetToCreateUoms && selectedYetToCreateUoms.length > 0) {
            filtered = filtered.filter(r => selectedYetToCreateUoms.includes(r.yetToCreateBatchUom));
        }
        if (selectedYetToInwardUoms && selectedYetToInwardUoms.length > 0) {
            filtered = filtered.filter(r => selectedYetToInwardUoms.includes(r.yetToInwardUom));
        }
        if (selectedInPosUoms && selectedInPosUoms.length > 0) {
            filtered = filtered.filter(r => selectedInPosUoms.includes(r.yetToCreateBatchUom));
        }
        if (selectedTotalWipUoms && selectedTotalWipUoms.length > 0) {
            filtered = filtered.filter(r => selectedTotalWipUoms.includes(r.yetToCreateBatchUom));
        }
        return filtered;
    }, [allRows, selectedByProducts, selectedRawMaterials, selectedRmUoms, selectedYetToCreateUoms, selectedYetToInwardUoms, selectedInPosUoms, selectedTotalWipUoms]);

    const bpOptions = React.useMemo(() => {
        const uniqueNames = Array.from(new Set(allRows.map(r => r.byProductName)));
        return uniqueNames.map(name => ({ label: name, value: name })).sort((a, b) => a.label.localeCompare(b.label));
    }, [allRows]);

    const rmFilterOptions = React.useMemo(() => {
        const uniqueRMs = new Set<string>();
        allRows.forEach(r => r.mappedRMs.forEach(rm => uniqueRMs.add(rm.name)));
        return Array.from(uniqueRMs).map(name => ({ label: name, value: name })).sort((a, b) => a.label.localeCompare(b.label));
    }, [allRows]);

    const uomFilterOptions = React.useMemo(() => {
        const uniqueUoms = new Set<string>();
        allRows.forEach(r => {
            if (r.rmUom) uniqueUoms.add(r.rmUom);
            if (r.yetToCreateBatchUom) uniqueUoms.add(r.yetToCreateBatchUom);
            if (r.yetToInwardUom) uniqueUoms.add(r.yetToInwardUom);
        });
        uniqueUoms.add("pcs");
        return Array.from(uniqueUoms).map(u => ({ label: u, value: u })).sort((a, b) => a.label.localeCompare(b.label));
    }, [allRows]);

    const bpHeader = (
        <>
            <span className="rm-header-label">BY PRODUCT</span>
            <div className="rm-header-filter-container">
                <MultiSelect
                    value={selectedByProducts}
                    options={bpOptions}
                    onChange={(e) => setSelectedByProducts(e.value)}
                    placeholder="Search Byproduct"
                    filter
                    maxSelectedLabels={1}
                    className="rm-header-filter"
                    style={{ border: selectedByProducts.length > 0 ? '1px solid #22c55e' : '' }}
                />
            </div>
        </>
    );

    const rmHeader = (
        <>
            <span className="rm-header-label">AVAILABLE STOCK(RM)</span>
            <div className="rm-header-filter-container">
                <MultiSelect
                    value={selectedRawMaterials}
                    options={rmFilterOptions}
                    onChange={(e) => setSelectedRawMaterials(e.value)}
                    placeholder="Search Material"
                    filter
                    maxSelectedLabels={1}
                    className="rm-header-filter"
                    style={{ border: selectedRawMaterials.length > 0 ? '1px solid #22c55e' : '' }}
                />
            </div>
        </>
    );

    const rmUomHeader = (
        <>
            <span className="rm-header-label" style={{ fontSize: '0.72rem', fontWeight: 'bold' }}>UOM</span>
            <div className="rm-header-filter-container" style={{ marginTop: '4px' }}>
                <MultiSelect
                    value={selectedRmUoms}
                    options={uomFilterOptions}
                    onChange={(e) => setSelectedRmUoms(e.value)}
                    placeholder="UOM"
                    filter
                    maxSelectedLabels={5}
                    className={`rm-header-filter uom-filter-custom ${selectedRmUoms.length > 0 ? 'uom-active' : ''}`}
                    style={{ 
                        height: '22px', 
                        width: '100%', 
                        border: selectedRmUoms.length > 0 ? '1px solid #22c55e' : ''
                    }}
                    panelStyle={{ minWidth: '120px' }}
                />
            </div>
        </>
    );

    const yetToCreateUomHeader = (
        <>
            <span className="rm-header-label" style={{ fontSize: '0.72rem', fontWeight: 'bold' }}>UOM</span>
            <div className="rm-header-filter-container" style={{ marginTop: '4px' }}>
                <MultiSelect
                    value={selectedYetToCreateUoms}
                    options={uomFilterOptions}
                    onChange={(e) => setSelectedYetToCreateUoms(e.value)}
                    placeholder="UOM"
                    filter
                    maxSelectedLabels={5}
                    className={`rm-header-filter uom-filter-custom ${selectedYetToCreateUoms.length > 0 ? 'uom-active' : ''}`}
                    style={{ 
                        height: '22px', 
                        width: '100%', 
                        border: selectedYetToCreateUoms.length > 0 ? '1px solid #22c55e' : ''
                    }}
                    panelStyle={{ minWidth: '120px' }}
                />
            </div>
        </>
    );

    const yetToInwardUomHeader = (
        <>
            <span className="rm-header-label" style={{ fontSize: '0.72rem', fontWeight: 'bold' }}>UOM</span>
            <div className="rm-header-filter-container" style={{ marginTop: '4px' }}>
                <MultiSelect
                    value={selectedYetToInwardUoms}
                    options={uomFilterOptions}
                    onChange={(e) => setSelectedYetToInwardUoms(e.value)}
                    placeholder="UOM"
                    filter
                    maxSelectedLabels={5}
                    className={`rm-header-filter uom-filter-custom ${selectedYetToInwardUoms.length > 0 ? 'uom-active' : ''}`}
                    style={{ 
                        height: '22px', 
                        width: '100%', 
                        border: selectedYetToInwardUoms.length > 0 ? '1px solid #22c55e' : ''
                    }}
                    panelStyle={{ minWidth: '120px' }}
                />
            </div>
        </>
    );

    const inPosUomHeader = (
        <>
            <span className="rm-header-label" style={{ fontSize: '0.72rem', fontWeight: 'bold' }}>UOM</span>
            <div className="rm-header-filter-container" style={{ marginTop: '4px' }}>
                <MultiSelect
                    value={selectedInPosUoms}
                    options={uomFilterOptions}
                    onChange={(e) => setSelectedInPosUoms(e.value)}
                    placeholder="UOM"
                    filter
                    maxSelectedLabels={5}
                    className={`rm-header-filter uom-filter-custom ${selectedInPosUoms.length > 0 ? 'uom-active' : ''}`}
                    style={{ 
                        height: '22px', 
                        width: '100%', 
                        border: selectedInPosUoms.length > 0 ? '1px solid #22c55e' : ''
                    }}
                    panelStyle={{ minWidth: '120px' }}
                />
            </div>
        </>
    );

    const totalWipUomHeader = (
        <>
            <span className="rm-header-label" style={{ fontSize: '0.72rem', fontWeight: 'bold' }}>UOM</span>
            <div className="rm-header-filter-container" style={{ marginTop: '4px' }}>
                <MultiSelect
                    value={selectedTotalWipUoms}
                    options={uomFilterOptions}
                    onChange={(e) => setSelectedTotalWipUoms(e.value)}
                    placeholder="UOM"
                    filter
                    maxSelectedLabels={5}
                    className={`rm-header-filter uom-filter-custom ${selectedTotalWipUoms.length > 0 ? 'uom-active' : ''}`}
                    style={{ 
                        height: '22px', 
                        width: '100%', 
                        border: selectedTotalWipUoms.length > 0 ? '1px solid #22c55e' : ''
                    }}
                    panelStyle={{ minWidth: '120px' }}
                />
            </div>
        </>
    );

    const remainingBody = (r: FlatRow) => {
        let val = r.rmStock;
        let mappedToShow = r.mappedRMs;
        let valueAmt = r.mappedRMs.reduce((acc, curr) => acc + curr.value, 0);

        if (selectedRawMaterials && selectedRawMaterials.length > 0) {
            mappedToShow = r.mappedRMs.filter(m => selectedRawMaterials.includes(m.name));
            val = mappedToShow.reduce((acc, curr) => acc + curr.stock, 0);
            valueAmt = mappedToShow.reduce((acc, curr) => acc + curr.value, 0);
        }

        const sev = val > 20 ? 'success' : val > 0 ? 'warning' : 'danger';
        const fv = (n: number) => n.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: n < 1000 ? 2 : 0 });

        return (
            <div className="hover-wrapper" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                <Tag value={f(val)} severity={sev} className="compact-tag" />
                {valueAmt > 0 && (
                    <span className="stock-value-badge">
                        <span style={{ fontSize: '0.85em', marginRight: '2px' }}>₹</span>{fv(valueAmt)}
                    </span>
                )}
                {mappedToShow.length > 0 && (
                    <SmartTooltip
                        title="Raw Materials"
                        value={val}
                        unit="KG"
                        items={mappedToShow}
                        renderItem={(rm, idx) => (
                            <div key={idx} className="tt-ap">
                                <div className="tt-ap-name">
                                    <span className="tt-ap-lbl">
                                        <i className="pi pi-angle-right"></i> {rm.name}
                                    </span>
                                    <span className="tt-ap-avail">{f(rm.stock)} <small>KG</small></span>
                                </div>
                                {rm.value > 0 && (
                                    <div style={{ textAlign: 'right', fontSize: '0.65rem', color: '#86efac', marginTop: '2px' }}>
                                        ₹{fv(rm.value)}
                                    </div>
                                )}
                            </div>
                        )}
                    />
                )}
            </div>
        );
    };

    const hoverCell = (row: FlatRow, field: 'yetToCreateBatch' | 'yetToInward' | 'inPos') => {
        const unit = row.unit?.toLowerCase() === 'litre' ? 'L' : row.unit;

        let displayVal = row[field];
        let batchesToShow = row.productionBatches;
        let bpsToShow = row.byProducts;
        let valueAmt = 0;

        if (field === 'yetToCreateBatch') valueAmt = row.pendingValue;
        else if (field === 'yetToInward') valueAmt = row.batchAvailableValue;
        else if (field === 'inPos') valueAmt = row.posValue;

        if (selectedRawMaterials && selectedRawMaterials.length > 0) {
            batchesToShow = row.productionBatches.filter(b => selectedRawMaterials.includes(b.rmName));
            bpsToShow = row.byProducts.filter(bp => selectedRawMaterials.includes(bp.rmName));

            if (field === 'inPos') {
                displayVal = bpsToShow.reduce((acc, curr) => {
                    const bpPosSum = (curr.allocatedProducts ?? []).reduce((sum, ap) => sum + (ap.availableStock || 0), 0);
                    return acc + bpPosSum;
                }, 0);
                valueAmt = bpsToShow.reduce((acc, curr) => acc + (curr.poInwardedValue || 0), 0);
            } else {
                const mapField = field === 'yetToCreateBatch' ? 'yetToCreateBatch' : 'yetToInward';
                displayVal = bpsToShow.reduce((acc, curr) => acc + (curr[mapField] || 0), 0);
                const valField = field === 'yetToCreateBatch' ? 'pendingValue' : 'batchAvailableValue';
                valueAmt = bpsToShow.reduce((acc, curr) => acc + (curr[valField] || 0), 0);
            }
        } else {
            if (field === 'inPos') {
                displayVal = row.byProducts.reduce((acc, curr) => {
                    const bpPosSum = (curr.allocatedProducts ?? []).reduce((sum, ap) => sum + (ap.availableStock || 0), 0);
                    return acc + bpPosSum;
                }, 0);
            }
        }

        const volumeL = field === 'inPos' ? sumPos(bpsToShow) : 0;
        const fv = (n: number) => n.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: n < 1000 ? 2 : 0 });

        const ValueBadge = ({ amt }: { amt: number }) =>
            amt > 0 ? (
                <span className="stock-value-badge">
                    <span style={{ fontSize: '0.85em', marginRight: '2px' }}>₹</span>{fv(amt)}
                </span>
            ) : null;

        if (displayVal <= 0 && field !== 'yetToCreateBatch') return <span className="zero-val">—</span>;
        if (displayVal === 0 && field === 'yetToCreateBatch') return <span className="zero-val">—</span>;

        return (
            <div className={`hover-wrapper ${displayVal < 0 ? 'neg' : ''}`} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                <span className="hover-val">
                    {displayVal < 0 && '-'} {f(Math.abs(displayVal))}
                    <i className="pi pi-info-circle"></i>
                </span>
                <ValueBadge amt={valueAmt} />

                {field === 'yetToCreateBatch' && (
                    <SmartTooltip
                        title={row.byProductName}
                        value={displayVal}
                        unit={unit}
                        items={bpsToShow.filter(bp => bp.yetToCreateBatch > 0)}
                        renderItem={(bp, idx) => (
                            <div key={idx} className="tt-ap">
                                <div className="tt-ap-name">
                                    <span className="tt-ap-lbl">
                                        <i className="pi pi-angle-right"></i> {bp.rmName}
                                    </span>
                                    <span className="tt-ap-avail">{f(bp.yetToCreateBatch)}{unit} <small>Pending</small></span>
                                </div>
                                {bp.pendingValue > 0 && (
                                    <div style={{ textAlign: 'right', fontSize: '0.65rem', color: '#86efac', marginTop: '2px' }}>
                                        ₹{fv(bp.pendingValue)}
                                    </div>
                                )}
                            </div>
                        )}
                    />
                )}

                {field === 'yetToInward' && (
                    <SmartTooltip
                        title={row.byProductName}
                        value={displayVal}
                        unit={unit}
                        items={batchesToShow.filter(b => b.batchAvailableStock > 0)}
                        renderItem={(b) => (
                            <div key={b.batchId} className="tt-ap">
                                <div className="tt-ap-name">
                                    <span className="tt-ap-lbl">
                                        <i className="pi pi-angle-right"></i> Batch #{b.batchId} <small>({b.sourceTypeName})</small>
                                    </span>
                                    <span className="tt-ap-avail">{f(b.batchAvailableStock)}{unit} <small>Avail.</small></span>
                                </div>
                                <div className="tt-pos-tags">
                                    <span className="tt-pos-tag" style={{ background: 'transparent', border: 'none', color: '#94a3b8', fontSize: '0.65rem', padding: '0' }}>
                                        Total Stock: {f(b.batchStock)} {unit}
                                    </span>
                                    {b.batchAvailableStock > 0 && (
                                        <span className="tt-pos-tag" style={{ background: 'transparent', border: 'none', color: '#86efac', fontSize: '0.65rem', padding: '0' }}>
                                            <span style={{ fontSize: '0.85em', marginRight: '1px' }}>₹</span>{fv(b.batchAvailableStock * (row.byProducts.find(bp => bp.batches.some(bt => bt.batchId === b.batchId))?.batchAvailableValue || 0) / (row.byProducts.find(bp => bp.batches.some(bt => bt.batchId === b.batchId))?.batchAvailableStock || 1))}
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}
                    />
                )}

                {field === 'inPos' && (() => {
                    const productMap = new Map<number, AllocatedProduct>();
                    bpsToShow.forEach(bp => {
                        (bp.allocatedProducts ?? []).forEach(ap => {
                            if (!productMap.has(ap.productId)) {
                                productMap.set(ap.productId, JSON.parse(JSON.stringify(ap)));
                            } else {
                                const existing = productMap.get(ap.productId)!;
                                existing.availableStock += ap.availableStock;
                                existing.allocatedVolume += (ap.allocatedVolume || 0);
                                (ap.posAllocations ?? []).forEach(pos => {
                                    const existingPos = existing.posAllocations.find(p => p.posId === pos.posId);
                                    if (existingPos) {
                                        existingPos.availableStock += pos.availableStock;
                                        existingPos.stock += pos.stock;
                                    } else {
                                        existing.posAllocations.push({ ...pos });
                                    }
                                });
                            }
                        });
                    });

                    const uniqueProducts = Array.from(productMap.values()).filter(p => p.availableStock > 0);

                    return (
                        <SmartTooltip
                            title={row.byProductName}
                            value={displayVal}
                            unit="pcs"
                            subtitle={`${f(volumeL)} ${unit}`}
                            items={uniqueProducts}
                            renderItem={(ap: AllocatedProduct) => (
                                <div key={ap.productId} className="tt-ap">
                                    <div className="tt-ap-name" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <span className="tt-ap-lbl">
                                            <i className="pi pi-angle-right"></i> {ap.productName}
                                        </span>
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                            <span className="tt-ap-avail">
                                                {f(ap.availableStock)} <small>pcs</small>
                                            </span>
                                            <span style={{ fontSize: '0.65rem', color: '#94a3b8', marginTop: '2px', fontWeight: '500' }}>
                                                {f(ap.allocatedVolume)} {unit}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="tt-pos-tags">
                                        {(ap.posAllocations ?? []).filter((pos: PosAllocation) => pos.availableStock > 0).map((pos: PosAllocation) => (
                                            <span key={pos.posId} className="tt-pos-tag">
                                                {pos.posName}: {f(pos.availableStock)} pcs
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        />
                    );
                })()}
            </div>
        );
    };

    const exportCSV = () => {
        const rows: string[] = [];
        const esc = (val: any) => {
            const str = (val === null || val === undefined) ? '' : String(val);
            return `"${str.replace(/"/g, '""')}"`;
        };

        // Header matching UI columns exactly
        const headers = [
            "By Product", "Available stock(RM)", "UOM",
            "Yet to Create Batch", "UOM", "Yet to Inward", "UOM",
            "In POS", "UOM", "Total WIP Qty", "UOM", "Total WIP Value"
        ];
        rows.push(headers.map(esc).join(','));

        displayRows.forEach((r, idx) => {
            const unit = r.unit?.toLowerCase() === 'litre' ? 'L' : r.unit;

            // Filtering raw material name & stock based on multiselect
            let valRmStock = r.rmStock;
            if (selectedRawMaterials && selectedRawMaterials.length > 0) {
                const mappedToShow = r.mappedRMs.filter(m => selectedRawMaterials.includes(m.name));
                valRmStock = mappedToShow.reduce((acc, curr) => acc + curr.stock, 0);
            }

            let yetToCreate = r.yetToCreateBatch;
            let yetToInw = r.yetToInward;
            let inPosStock = r.inPos;
            let pendingVal = r.pendingValue;
            let batchAvailVal = r.batchAvailableValue;

            if (selectedRawMaterials && selectedRawMaterials.length > 0) {
                const bpsToShow = r.byProducts.filter(bp => selectedRawMaterials.includes(bp.rmName));
                yetToCreate = bpsToShow.reduce((acc, curr) => acc + (curr.yetToCreateBatch || 0), 0);
                yetToInw = bpsToShow.reduce((acc, curr) => acc + (curr.yetToInward || 0), 0);
                inPosStock = bpsToShow.reduce((acc, curr) => {
                    const bpPosSum = (curr.allocatedProducts ?? []).reduce((sum, ap) => sum + (ap.availableStock || 0), 0);
                    return acc + bpPosSum;
                }, 0);
                pendingVal = bpsToShow.reduce((acc, curr) => acc + (curr.pendingValue || 0), 0);
                batchAvailVal = bpsToShow.reduce((acc, curr) => acc + (curr.batchAvailableValue || 0), 0);
            } else {
                inPosStock = r.byProducts.reduce((acc, curr) => {
                    const bpPosSum = (curr.allocatedProducts ?? []).reduce((sum, ap) => sum + (ap.availableStock || 0), 0);
                    return acc + bpPosSum;
                }, 0);
            }

            const totalWip = yetToCreate + yetToInw;
            const totalValue = pendingVal + batchAvailVal;

            const rowData = [
                r.byProductName,
                valRmStock.toFixed(2),
                r.rmUom,
                yetToCreate.toFixed(2),
                unit,
                yetToInw.toFixed(2),
                unit,
                inPosStock.toFixed(2),
                "pcs",
                totalWip.toFixed(2),
                unit,
                totalValue.toFixed(2)
            ];

            rows.push(rowData.map(esc).join(','));
        });

        const blob = new Blob(['\uFEFF' + rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `WIP_Dashboard_Main_${new Date().toISOString().split('T')[0]}.csv`;
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const headerGroup = (
        <ColumnGroup>
            <Row>
                {/* <Column header="Sl No." className="col-rm" style={{ width: '3%', textAlign: 'center' }} /> */}
                <Column header={bpHeader} className="col-rm" style={{ width: '21%' }} sortable field="byProductName" />
                <Column header={rmHeader} className="col-stock" style={{ width: '12%' }} sortable field="rmStock" />
                <Column header={rmUomHeader} className="col-stock" style={{ width: '3%', textAlign: 'center' }} />
                <Column header="Yet to Create Batch" className="col-stock" style={{ width: '10%' }} sortable field="yetToCreateBatch" />
                <Column header={yetToCreateUomHeader} className="col-stock" style={{ width: '3%', textAlign: 'center' }} />
                <Column header="Yet to Inward" className="col-stock" style={{ width: '10%' }} sortable field="yetToInward" />
                <Column header={yetToInwardUomHeader} className="col-stock" style={{ width: '3%', textAlign: 'center' }} />
                <Column header="In POS" className="col-stock" style={{ width: '11%' }} sortable field="inPos" />
                <Column header={inPosUomHeader} className="col-stock" style={{ width: '3%', textAlign: 'center' }} />
                <Column header="Total WIP Qty" className="col-stock" style={{ width: '10%' }} />
                <Column header={totalWipUomHeader} className="col-stock" style={{ width: '3%', textAlign: 'center' }} />
                {/* <Column header="Unit Price" className="col-stock" style={{ width: '8%' }} /> */}
                <Column header="Total WIP Value" className="col-stock" style={{ width: '11%' }} />
            </Row>
        </ColumnGroup>
    );

    if (initialLoading) return (
        <div className="rmd-center-spin">
            <ProgressSpinner style={{ width: 44, height: 44 }} strokeWidth="3" animationDuration=".5s" />
        </div>
    );

    return (
        <div className="rmd-root">
            <style>{`
                .uom-filter-custom.p-multiselect {
                    min-width: 40px !important;
                }
                .uom-filter-custom .p-multiselect-label {
                    font-size: 0.55rem !important;
                    padding: 0px 2px !important;
                    line-height: 20px !important;
                    text-align: center !important;
                    text-transform: lowercase !important;
                }
                .uom-filter-custom .p-multiselect-trigger {
                    width: 12px !important;
                }
                .uom-filter-custom.uom-active .p-multiselect-trigger {
                    display: none !important;
                }
                .uom-filter-custom.uom-active .p-multiselect-label {
                    padding-right: 2px !important;
                }
            `}</style>
            <div className="rmd-header">
                <div className="rmd-header__left">
                    <div className="rmd-header__icon"><i className="pi pi-database"></i></div>
                    <div>
                        <h1 className="rmd-header__title">Work In-Progress Dashboard</h1>
                        <p className="rmd-header__sub">Materials · Oil &amp; Cake Byproducts · POS Allocations</p>
                    </div>
                </div>
                <div className="rmd-filter">
                    <label className="rmd-filter__label">Manufacturing Unit</label>
                    <MultiSelect
                        value={selectedPosIds} options={posOptions}
                        onChange={e => setSelectedPosIds(e.value)}
                        placeholder="Select Units" maxSelectedLabels={3} display="chip"
                        className="rmd-multiselect"
                    />
                    <FileActionButton action="export"
                        icon="pi pi-file-excel"
                        label="Export"
                        severity="success"
                        outlined
                        onClick={exportCSV}
                        disabled={displayRows.length === 0}
                        style={{ padding: '0.4rem 1rem', fontSize: '0.8rem', marginLeft: '0.5rem', borderRadius: '8px' }}
                    />
                </div>
            </div>

            <div className="rmd-content">
                <div className="rmd-table-card">
                    <DataTable
                        value={displayRows}
                        loading={loading}
                        headerColumnGroup={headerGroup}
                        dataKey="byProductName"
                        className="rmd-datatable p-datatable-sm"
                        emptyMessage="No data for selected units."
                        stripedRows
                        rowHover
                        paginator
                        first={first}
                        onPage={(e) => {
                            setFirst(e.first);
                            setRowsPerPage(e.rows);
                        }}
                        rows={rowsPerPage}
                        rowsPerPageOptions={[15, 30, 50]}
                        rowClassName={(data: FlatRow) => {
                            const globalIdx = displayRows.findIndex(r => r.byProductName === data.byProductName);
                            const pageIdx = globalIdx - first;
                            const rowsOnCurrentPage = Math.min(rowsPerPage, displayRows.length - first);
                            const isBottom = (pageIdx >= rowsOnCurrentPage - 3 || globalIdx >= displayRows.length - 3) &&
                                pageIdx >= 3 &&
                                displayRows.length >= 6;
                            return {
                                'row-oil': data.isOil,
                                'row-cake': !data.isOil,
                                'row-bottom': isBottom
                            };
                        }}
                        tableStyle={{ width: '100%', tableLayout: 'auto' }}
                        showGridlines
                    >
                        {/* Sl No. */}
                        {/* <Column style={{ width: '3%' }} body={(data: FlatRow, options) => (
                            <div style={{ textAlign: 'center' }}>
                                {options.rowIndex + 1}
                            </div>
                        )} /> */}

                        {/* By Product Name */}
                        <Column style={{ width: '21%' }} field="byProductName" sortable bodyClassName="rm-name-cell"
                            body={(r: FlatRow) => (
                                <div className="bp-name-wrapper">
                                    <i className={`pi ${r.isOil ? 'pi-tint' : 'pi-box'}`}
                                        style={{ color: r.isOil ? '#f59e0b' : '#8b5cf6', marginRight: '8px', fontSize: '0.9rem' }}></i>
                                    {r.byProductName}
                                </div>
                            )} />

                        {/* Available Stock (RM) */}
                        <Column style={{ width: '12%' }} field="rmStock" sortable align="right" body={remainingBody} />

                        {/* RM Stock UOM */}
                        <Column style={{ width: '3%' }} body={(r: FlatRow) => (
                            <div style={{ textAlign: 'center', fontWeight: '500' }}>
                                {r.rmUom}
                            </div>
                        )} />

                        {/* Yet to Create Batch */}
                        <Column style={{ width: '10%' }} field="yetToCreateBatch" sortable align="right" bodyClassName={(r: FlatRow) => `val-cell ${r.isOil ? 'val-cell--oil' : 'val-cell--cake'}`}
                            body={(r: FlatRow) => hoverCell(r, 'yetToCreateBatch')} />

                        {/* Yet to Create Batch UOM */}
                        <Column style={{ width: '3%' }} body={(r: FlatRow) => (
                            <div style={{ textAlign: 'center', fontWeight: '500' }}>
                                {r.yetToCreateBatchUom}
                            </div>
                        )} />

                        {/* Yet to Inward */}
                        <Column style={{ width: '10%' }} field="yetToInward" sortable align="right" bodyClassName={(r: FlatRow) => `val-cell ${r.isOil ? 'val-cell--oil' : 'val-cell--cake'}`}
                            body={(r: FlatRow) => hoverCell(r, 'yetToInward')} />

                        {/* Yet to Inward UOM */}
                        <Column style={{ width: '3%' }} body={(r: FlatRow) => (
                            <div style={{ textAlign: 'center', fontWeight: '500' }}>
                                {r.yetToInwardUom}
                            </div>
                        )} />

                        {/* In POS */}
                        <Column style={{ width: '11%' }} field="inPos" sortable align="right" bodyClassName={(r: FlatRow) => `val-cell ${r.isOil ? 'val-cell--oil' : 'val-cell--cake'}`}
                            body={(r: FlatRow) => hoverCell(r, 'inPos')} />

                        {/* In POS UOM */}
                        <Column style={{ width: '3%' }} body={() => (
                            <div style={{ textAlign: 'center', fontWeight: '500' }}>
                                pcs
                            </div>
                        )} />

                        {/* Total WIP Qty */}
                        <Column style={{ width: '10%' }} body={(r: FlatRow) => {
                            let yetToCreate = r.yetToCreateBatch;
                            let yetToInw = r.yetToInward;
                            if (selectedRawMaterials && selectedRawMaterials.length > 0) {
                                const bpsToShow = r.byProducts.filter(bp => selectedRawMaterials.includes(bp.rmName));
                                yetToCreate = bpsToShow.reduce((acc, curr) => acc + (curr.yetToCreateBatch || 0), 0);
                                yetToInw = bpsToShow.reduce((acc, curr) => acc + (curr.yetToInward || 0), 0);
                            }
                            return (
                                <div style={{ textAlign: 'right', fontWeight: 'bold' }}>
                                    {f(yetToCreate + yetToInw)}
                                </div>
                            );
                        }} />

                        {/* Total WIP Qty UOM */}
                        <Column style={{ width: '3%' }} body={(r: FlatRow) => (
                            <div style={{ textAlign: 'center', fontWeight: '500' }}>
                                {r.yetToCreateBatchUom}
                            </div>
                        )} />

                        {/* Unit Price */}
                        {/* <Column style={{ width: '8%' }} body={(r: FlatRow) => (
                            <div style={{ textAlign: 'right', color: '#10b981', fontWeight: '600' }}>
                                ₹{f(r.unitPrice)}
                            </div>
                        )} /> */}

                        {/* Total WIP Value */}
                        <Column style={{ width: '11%' }} body={(r: FlatRow) => {
                            let pendingVal = r.pendingValue;
                            let batchAvailVal = r.batchAvailableValue;
                            if (selectedRawMaterials && selectedRawMaterials.length > 0) {
                                const bpsToShow = r.byProducts.filter(bp => selectedRawMaterials.includes(bp.rmName));
                                pendingVal = bpsToShow.reduce((acc, curr) => acc + (curr.pendingValue || 0), 0);
                                batchAvailVal = bpsToShow.reduce((acc, curr) => acc + (curr.batchAvailableValue || 0), 0);
                            }
                            const totalValue = pendingVal + batchAvailVal;
                            return (
                                <div style={{ textAlign: 'right', color: '#3b82f6', fontWeight: 'bold' }}>
                                    ₹{f(totalValue)}
                                </div>
                            );
                        }} />
                    </DataTable>
                </div>
            </div>
        </div>
    );
};

export default WipProductionMain;
